import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { fmtMoney } from '../lib/format';
import { useCan } from '../lib/auth';
import { useOpenDocument } from './Layout';
import { Button, Callout, Dialog, useToast } from './ui';

type Env = 'sandbox' | 'production' | 'preview_only';
type IssueStatus = { target: 'off' | 'sandbox' | 'production' };
export type IssuedInfo = {
  status: 'draft' | 'issuing' | 'issued' | 'failed' | 'cancelled';
  morningNumber: string | null;
  morningUrl: string | null;
  issuedEnvironment: 'sandbox' | 'production' | null;
  emailedTo: string | null;
  documentId: number | null;
  issueError: string | null;
};

const TYPE_LABEL = { quote: 'הצעת מחיר', delivery_note: 'תעודת משלוח', tax_invoice: 'חשבונית מס', tax_invoice_receipt: 'חשבונית מס/קבלה' } as const;

/** Israeli phone → wa.me number (050-1234567 → 972501234567). */
export function whatsappNumber(phone: string | null | undefined) {
  const d = (phone ?? '').replace(/\D/g, '');
  if (!d) return null;
  if (d.startsWith('972')) return d;
  if (d.startsWith('0')) return `972${d.slice(1)}`;
  return d.length >= 9 ? `972${d}` : null;
}

export function whatsappLink(phone: string | null | undefined, text: string) {
  const n = whatsappNumber(phone);
  return `https://wa.me/${n ?? ''}?text=${encodeURIComponent(text)}`;
}

/**
 * "Preview and issue": Morning renders the exact document; only "approve and issue" creates it.
 * `saveNow` makes sure the server has the latest lines before the preview.
 */
export function IssueOrder({
  orderId,
  docType,
  total,
  customer,
  saveNow,
  info,
  onIssued,
}: {
  orderId: number;
  docType: keyof typeof TYPE_LABEL;
  total: number;
  customer: { email: string | null; phone: string | null; businessName: string };
  saveNow: () => Promise<boolean>;
  info: IssuedInfo;
  onIssued: () => void;
}) {
  const can = useCan();
  const toast = useToast();
  const qc = useQueryClient();
  const openDoc = useOpenDocument();
  const issue = useQuery({ queryKey: ['morning-issue'], queryFn: () => api<IssueStatus>('/api/integrations/morning/issue') });
  const [preview, setPreview] = useState<{ url: string; hash: string; env: Env } | null>(null);
  const [busy, setBusy] = useState<'preview' | 'issue' | null>(null);
  const [result, setResult] = useState<{ number: string; url: string | null; environment: 'sandbox' | 'production'; emailedTo: string | null; documentId: number | null; fileNote: string | null } | null>(null);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview.url)), [preview]);

  const target = issue.data?.target ?? 'off';
  const canIssue = can('documents.issue') && target !== 'off';

  const openPreview = async () => {
    setBusy('preview');
    try {
      if (!(await saveNow())) return;
      const res = await fetch(`/api/orders/${orderId}/preview`, { method: 'POST', headers: { 'X-Requested-With': 'markit' }, credentials: 'same-origin' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(res.status, (body as { message?: string }).message ?? `שגיאה (${res.status})`);
      }
      const blob = await res.blob();
      setPreview({ url: URL.createObjectURL(blob), hash: res.headers.get('X-Preview-Hash') ?? '', env: (res.headers.get('X-Issue-Environment') as Env) ?? 'preview_only' });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  const doIssue = async (hash: string) => {
    setBusy('issue');
    try {
      const r = await api<NonNullable<typeof result>>(`/api/orders/${orderId}/issue`, { body: { hash } });
      setResult(r);
      setPreview(null);
      for (const k of ['orders', 'order', 'documents', 'customer', 'price-list']) qc.invalidateQueries({ queryKey: [k] });
      onIssued();
    } catch (e) {
      toast.error(e);
      qc.invalidateQueries({ queryKey: ['order', orderId] });
    } finally {
      setBusy(null);
    }
  };

  const shown =
    result ??
    (info.status === 'issued' && info.morningNumber
      ? { number: info.morningNumber, url: info.morningUrl, environment: info.issuedEnvironment ?? 'production', emailedTo: info.emailedTo, documentId: info.documentId, fileNote: null }
      : null);

  if (shown) {
    const label = `${TYPE_LABEL[docType]} ${shown.number}`;
    const waText = `שלום, מצורפת ${label} ממרקיט ציוד משרדי${shown.url ? `: ${shown.url}` : ''}`;
    return (
      <section className="panel panel-body stack" style={{ gap: 10 }}>
        <Callout tone={shown.environment === 'sandbox' ? 'warn' : 'ok'} title={`${label} הופקה${shown.environment === 'sandbox' ? ' ב-Sandbox' : ''}`}>
          {shown.environment === 'sandbox' ? 'מסמך בדיקה — לא אמיתי ולא נכנס להנהלת החשבונות.' : shown.emailedTo ? `נשלחה במייל ל-${shown.emailedTo}.` : 'ללקוח אין מייל — אפשר לשלוח ב-WhatsApp.'}
          {shown.fileNote ? <div className="small" style={{ marginTop: 4 }}>{shown.fileNote}</div> : null}
        </Callout>
        {shown.url ? (
          <a className="btn" href={shown.url} target="_blank" rel="noreferrer noopener">פתיחת המסמך (PDF)</a>
        ) : null}
        <a className="btn" href={whatsappLink(customer.phone, waText)} target="_blank" rel="noreferrer noopener">
          שליחה ב-WhatsApp{whatsappNumber(customer.phone) ? '' : ' (בחירת איש קשר)'}
        </a>
        {shown.documentId ? <Button onClick={() => openDoc(shown.documentId!)}>המסמך בתיק הלקוח</Button> : null}
      </section>
    );
  }

  return (
    <section className="panel panel-body stack" style={{ gap: 8 }}>
      {info.status === 'failed' ? (
        <Callout tone="bad" title="לא ידוע אם המסמך הופק">
          {info.issueError} — לחיצה על "ניסיון חוזר" בודקת קודם במורנינג אם המסמך כבר קיים, ורק אם לא — מפיקה.
          <div style={{ marginTop: 8 }}>
            <Button variant="primary" size="sm" loading={busy === 'issue'} disabled={!canIssue} onClick={() => doIssue('retry-after-failure')}>ניסיון חוזר</Button>
          </div>
        </Callout>
      ) : (
        <>
          <Button variant="primary" onClick={openPreview} loading={busy === 'preview'} disabled={info.status !== 'draft'}>
            תצוגה מקדימה {canIssue ? 'והפקה' : ''}
          </Button>
          {info.issueError ? <Callout tone="bad">{info.issueError}</Callout> : null}
          <div className="small muted">
            {target === 'off'
              ? 'הפקה במורנינג כבויה (הגדרות ← מורנינג). אפשר לראות תצוגה מקדימה של המסמך.'
              : !can('documents.issue')
                ? 'אין לך הרשאה להפיק מסמכים — אפשר לראות תצוגה מקדימה.'
                : target === 'sandbox'
                  ? 'הפקה לסביבת הבדיקות (Sandbox) — מסמכי בדיקה בלבד.'
                  : 'הפקה אמיתית — המסמך יישלח ללקוח.'}
          </div>
        </>
      )}

      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title={`תצוגה מקדימה — ${TYPE_LABEL[docType]} ל${customer.businessName}`}
        size="xl"
        footer={
          <>
            {canIssue ? (
              <Button variant={target === 'production' ? 'danger-solid' : 'primary'} loading={busy === 'issue'} onClick={() => preview && doIssue(preview.hash)}>
                {target === 'production' ? `אישור והפקה (${fmtMoney(total)})` : `הפקה ב-Sandbox (${fmtMoney(total)})`}
              </Button>
            ) : null}
            <Button onClick={() => setPreview(null)}>חזרה לעריכה</Button>
          </>
        }
      >
        {preview ? (
          <div className="stack" style={{ gap: 8 }}>
            {preview.env === 'sandbox' ? <Callout tone="warn">סביבת בדיקות — המסמך שיופק הוא מסמך בדיקה בלבד.</Callout> : null}
            {preview.env === 'preview_only' ? <Callout>תצוגה בלבד — ההפקה כבויה בהגדרות.</Callout> : null}
            {target === 'production' && canIssue ? (
              <Callout tone="warn">
                זה המסמך שיופק במורנינג{customer.email ? ` וישלח ל-${customer.email}` : ''}. {docType === 'tax_invoice' || docType === 'tax_invoice_receipt' ? 'מסמך מס אינו ניתן למחיקה — רק לביטול בחשבונית זיכוי.' : ''}
              </Callout>
            ) : null}
            {!customer.email ? <Callout tone="warn">ללקוח אין כתובת מייל — המסמך לא יישלח במייל. אחרי ההפקה אפשר לשלוח ב-WhatsApp.</Callout> : null}
            <iframe title="תצוגה מקדימה" src={preview.url} style={{ width: '100%', height: '70vh', border: '1px solid var(--line)', borderRadius: 'var(--r)' }} />
          </div>
        ) : null}
      </Dialog>
    </section>
  );
}
